import React, { useEffect, useState } from "react";
import { FileText, Loader2, UserCheck, Mail, Phone, MapPin } from "lucide-react";
import { meta } from "../lib/text";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { fmtDate } from "../lib/dates";
import { downloadPdf } from "./FileUpload";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { VettingTracker } from "./VettingTracker";

const Row = ({ icon: Icon, children }) => <p className="flex items-center gap-2 text-sm"><Icon size={14} className="shrink-0 text-crimson" /> <span className="truncate">{children}</span></p>;

const Applicant = ({ m, reg }) => (
  <div className="grid gap-3 rounded-xl bg-parchment-card p-4 sm:grid-cols-2" data-testid="approval-applicant">
    <div className="space-y-1">
      <p className="font-serif-display text-lg font-bold text-crimson">{m.name}</p>
      <p className="text-xs text-muted-foreground">{meta(m.lineage_status, m.hometown || reg?.akuapem_town, `registered ${fmtDate(reg?.submitted_at || m.created_at)}`)}</p>
      <Row icon={Mail}>{m.email || "no email"}</Row>
      {m.phone && <Row icon={Phone}>{m.phone}</Row>}
      {reg?.address && <Row icon={MapPin}>{reg.address}</Row>}
    </div>
    <div className="flex flex-col items-start justify-center gap-2 sm:items-end">
      {reg ? (
        <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadPdf(reg.pdf_url, `registration-${reg.tracking_id}.pdf`)} data-testid="approval-registration-pdf"><FileText size={14} /> Registration form {reg.tracking_id}</Button>
      ) : <span className="text-xs text-muted-foreground" data-testid="approval-no-registration">No online registration form on file</span>}
      {reg?.membership_category && <span className="rounded-full bg-gold/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-crimson">{reg.membership_category}</span>}
    </div>
  </div>
);

export function ApproveMemberDialog({ member, onClose, onApproved, onVettingChange }) {
  const [data, setData] = useState(null);
  const [vetting, setVetting] = useState(null);
  const [canVet, setCanVet] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!member) { setData(null); setVetting(null); return; }
    api.get(`/admin/member-tools/${member.id}/approval-preview`).then(({ data: d }) => { setData(d); setMsg(d.message); })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
    api.get("/admin/vetting-tracker").then(({ data: v }) => { setCanVet(v.can_edit); setVetting(v.items.find((i) => i.id === member.id) || { stage: "new", history: [] }); }).catch(() => {});
  }, [member]);
  const approve = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/member-tools/${member.id}/approve`, { message: msg });
      toast.success(`Approved · invite ${r.invite}${r.sms === "sent" ? " · SMS sent" : ""}`);
      onApproved?.(member); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="approve-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-serif-display text-crimson"><UserCheck size={20} className="text-palm" /> Review &amp; approve membership</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="animate-spin" /> : (
          <div className="space-y-4">
            <Applicant m={data.member} reg={data.registration} />
            <VettingTracker memberId={member.id} canEdit={canVet} initial={vetting} onChange={() => onVettingChange?.()} />
            <div>
              <Label>Congratulation message (sent with the set-password invite)</Label>
              <Textarea rows={8} value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 1500))} data-testid="approval-message" />
              <p className="text-right text-[11px] text-muted-foreground">{msg.length}/1500 · mentions the ${data.reg_fee} registration fee, info@okuapemmanma.org and that an officer will reach out</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="approval-cancel">Not yet</Button>
              <Button onClick={approve} disabled={busy || !data.member.email} className="bg-palm hover:bg-palm/90" data-testid="approval-approve-btn">{busy && <Loader2 size={14} className="mr-1 animate-spin" />} Approve &amp; send invite</Button>
            </DialogFooter>
            {!data.member.email && <p className="text-xs text-destructive">This member has no email on file — add one before approving so the invite can be sent.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
